import {
  type Accessor,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  untrack,
} from 'solid-js';
import {
  type ClubDay,
  type ClubNow,
  clubNowIn,
  msUntilNextClubMidnight,
} from '~/shared/lib/clubClock';
import { useClubZone } from '~/shared/lib/useClubZone';

const MIDNIGHT_SLACK_MS = 250;

export const useClubClock = (
  tickMs = 30_000,
): { now: Accessor<ClubNow>; today: Accessor<ClubDay> } => {
  const zone = useClubZone();
  const [instant, setInstant] = createSignal(Date.now());

  const ticker = setInterval(() => setInstant(Date.now()), tickMs);
  onCleanup(() => clearInterval(ticker));

  const now = createMemo(() => clubNowIn(zone(), instant()));
  const today = createMemo(() => now().today);

  let midnight: ReturnType<typeof setTimeout> | undefined;
  const armMidnight = (snapshot: ClubNow): void => {
    clearTimeout(midnight);
    const delay = msUntilNextClubMidnight(snapshot);
    if (!Number.isFinite(delay)) return;
    midnight = setTimeout(() => {
      const at = Date.now();
      setInstant(at);
      armMidnight(clubNowIn(untrack(zone), at));
    }, delay + MIDNIGHT_SLACK_MS);
  };
  createEffect(() => armMidnight(clubNowIn(zone(), untrack(instant))));
  onCleanup(() => clearTimeout(midnight));

  return { now, today };
};

export const createClubDefault = <T>(
  clock: { now: Accessor<ClubNow> },
  seed: (now: ClubNow) => T,
): [Accessor<T>, (next: T) => void] => {
  const [value, setValue] = createSignal<T>(seed(untrack(clock.now)));
  let touched = false;
  const zone = createMemo(() => clock.now().zone);
  createEffect(
    on(
      zone,
      () => {
        if (!touched) setValue(() => seed(untrack(clock.now)));
      },
      { defer: true },
    ),
  );
  return [
    value,
    (next: T) => {
      touched = true;
      setValue(() => next);
    },
  ];
};
